/**
 * Auth Utilities
 * 
 * Utilidades para autenticación con AWS Cognito
 */

import { fetchAuthSession, signIn, signOut, signUp, confirmSignUp, getCurrentUser } from 'aws-amplify/auth'
import type { SignUpOutput } from 'aws-amplify/auth'

// Re-export fetchAuthSession for use in other apps
export { fetchAuthSession }

export interface AuthUser {
  userId: string
  email: string
  tenantId?: string
  roles?: string[]
}

export interface AuthConfig {
  userPoolId: string
  userPoolClientId: string
  region: string
}

/**
 * Configura Amplify Auth
 */
export function configureAuth(config: AuthConfig) {
  // La configuración se hace en el contexto de la app
  // Esta función es solo para validación
  if (!config.userPoolId || !config.userPoolClientId || !config.region) {
    console.warn('Auth configuration is incomplete - using placeholder values for local development')
    console.warn('To use real AWS Cognito, deploy infrastructure and run: ./scripts/get-env-vars.sh dev')
    return
  }
  
  // Check if using placeholder values
  if (config.userPoolId.includes('PLACEHOLDER') || config.userPoolClientId.includes('placeholder')) {
    console.warn('Using placeholder AWS Cognito configuration - authentication will not work')
    console.warn('For real authentication, deploy infrastructure and update .env.local files')
    return
  }
}

/**
 * Obtiene el token de autenticación (ID Token para API Gateway)
 * Returns the raw ID token string - the caller should add "Bearer " prefix
 * when setting the Authorization header for API Gateway Cognito User Pools Authorizer
 */
export async function getAuthToken(forceRefresh: boolean = false): Promise<string | null> {
  // Development mode bypass - return mock token when VITE_DEV_MODE is true
  // The local backend server doesn't validate tokens, it injects mock auth claims
  const isDevelopmentMode = typeof import.meta !== 'undefined' &&
    import.meta.env?.MODE === 'development' &&
    import.meta.env?.VITE_DEV_MODE === 'true'

  if (isDevelopmentMode) {
    console.log('[getAuthToken] 🔧 Development mode: returning mock token')
    return 'dev-mock-token'
  }

  try {
    const session = await fetchAuthSession({ forceRefresh })
    const idToken = session.tokens?.idToken
    
    if (!idToken) {
      console.warn('[getAuthToken] No ID token in session')
      return null
    }
    
    // Check if token is expired or about to expire (within 60 seconds)
    const expirationTime = idToken.payload?.exp as number | undefined
    if (expirationTime) {
      const now = Math.floor(Date.now() / 1000)
      const timeUntilExpiry = expirationTime - now
      
      if (timeUntilExpiry < 0) {
        console.warn('[getAuthToken] ID token is expired, attempting to refresh...')
        // Token is expired, try to refresh
        const refreshedSession = await fetchAuthSession({ forceRefresh: true })
        return refreshedSession.tokens?.idToken?.toString() || null
      } else if (timeUntilExpiry < 60) {
        console.log(`[getAuthToken] ID token expires in ${timeUntilExpiry} seconds, refreshing proactively...`)
        // Token expires soon, refresh proactively
        const refreshedSession = await fetchAuthSession({ forceRefresh: true })
        return refreshedSession.tokens?.idToken?.toString() || null
      } else {
        console.log(`[getAuthToken] ID token is valid (expires in ${timeUntilExpiry} seconds)`)
      }
    }
    
    // Use idToken because it contains custom attributes like tenant_id
    // which are required by the backend and the Cognito Authorizer
    return idToken.toString() || null
  } catch (error) {
    console.error('[getAuthToken] Error fetching auth session:', error)
    return null
  }
}

/**
 * Inicia sesión
 */
export async function login(email: string, password: string): Promise<{
  error?: Error,
  nextStep?: string,
  challengeParameters?: Record<string, string>
}> {
  try {
    const { nextStep } = await signIn({
      username: email,
      password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    })
    return { nextStep: nextStep.signInStep }
  } catch (error: any) {
    // If there is already a signed in user, treat it as a success
    if (error?.name === 'UserAlreadyAuthenticatedException' || (error?.message && error.message.includes('There is already a signed in user'))) {
      return { nextStep: 'DONE' }
    }
    return { error }
  }
}

/**
 * Confirma una nueva contraseña (después de invitación)
 */
export async function confirmNewPassword(newPassword: string): Promise<{ error?: Error }> {
  try {
    const { confirmSignIn } = await import('aws-amplify/auth')
    await confirmSignIn({
      challengeResponse: newPassword
    })
    return {}
  } catch (error: any) {
    return { error }
  }
}

/**
 * Cierra sesión
 */
export async function logout(): Promise<void> {
  try {
    await signOut()
  } catch (error) {
    console.error('Error signing out:', error)
    throw error
  }
}

/**
 * Registra un nuevo usuario
 */
export async function register(
  email: string,
  password: string,
  tenantId?: string
): Promise<{ error?: Error; data?: SignUpOutput }> {
  try {
    const result = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          ...(tenantId && { 'custom:tenant_id': tenantId }),
        },
      },
    })
    return { data: result }
  } catch (error: any) {
    return { error }
  }
}

/**
 * Confirma el registro de un usuario
 */
export async function confirmRegistration(
  email: string,
  confirmationCode: string
): Promise<{ error?: Error }> {
  try {
    await confirmSignUp({
      username: email,
      confirmationCode,
    })
    return {}
  } catch (error: any) {
    return { error }
  }
}

/**
 * Obtiene el usuario actual
 */
export async function getCurrentAuthUser(): Promise<AuthUser | null> {
  try {
    let user
    try {
      user = await getCurrentUser()
    } catch (error: any) {
      // getCurrentUser puede fallar si no hay usuario autenticado
      // pero si hay tokens, podemos continuar
      console.warn('[getCurrentAuthUser] getCurrentUser failed:', error?.message || error)
      user = null
    }

    const session = await fetchAuthSession()
    if (!session.tokens) {
      console.warn('[getCurrentAuthUser] No session tokens found')
      return null
    }

    // Si getCurrentUser falló pero tenemos tokens, intentamos extraer info de los tokens
    if (!user && session.tokens) {
      console.log('[getCurrentAuthUser] getCurrentUser failed but we have tokens, extracting from tokens')
      const userId = session.tokens.idToken?.payload?.sub as string | undefined
      if (!userId) {
        console.warn('[getCurrentAuthUser] No userId found in tokens')
        return null
      }
      // Crear un objeto user mínimo desde los tokens
      user = { userId } as any
    }

    if (!user) {
      console.warn('[getCurrentAuthUser] No user object available')
      return null
    }

    // Extraer email del token o del username
    let email = user.username
    if (session.tokens?.idToken?.payload?.email) {
      email = session.tokens.idToken.payload.email as string
    }

    // Extraer tenant_id del token
    let tenantId = session.tokens?.accessToken?.payload?.['custom:tenant_id'] as string | undefined

    // DEBUG: Ver que atributos vienen realmente
    console.log('[getCurrentAuthUser] Session Payload:', session.tokens?.accessToken?.payload)

    if (!tenantId) {
      tenantId = session.tokens?.accessToken?.payload?.['custom:tenantId'] as string | undefined
    }

    // Check ID Token as well (custom attributes are often here)
    if (!tenantId) {
      tenantId = session.tokens?.idToken?.payload?.['custom:tenant_id'] as string | undefined
    }
    if (!tenantId) {
      tenantId = session.tokens?.idToken?.payload?.['custom:tenantId'] as string | undefined
    }

    console.log('[getCurrentAuthUser] Final extracted tenantId:', tenantId);

    // Extraer roles de los grupos de Cognito
    const groupsValue = session.tokens?.accessToken?.payload?.['cognito:groups']
    let roles: string[] = []

    console.log('[getCurrentAuthUser] Raw cognito:groups value:', groupsValue, 'Type:', typeof groupsValue)

    if (Array.isArray(groupsValue)) {
      roles = groupsValue as string[]
    } else if (typeof groupsValue === 'string') {
      roles = groupsValue.split(',').map((g: string) => g.trim())
    }

    // También verificar en idToken
    if (roles.length === 0) {
      const idTokenGroups = session.tokens?.idToken?.payload?.['cognito:groups']
      console.log('[getCurrentAuthUser] Checking idToken cognito:groups:', idTokenGroups)
      if (Array.isArray(idTokenGroups)) {
        roles = idTokenGroups as string[]
      } else if (typeof idTokenGroups === 'string') {
        roles = idTokenGroups.split(',').map((g: string) => g.trim())
      }
    }

    // Fallback: custom:role if no groups
    if (roles.length === 0) {
      const customRole = session.tokens?.accessToken?.payload?.['custom:role'] as string | undefined
      if (customRole) {
        roles.push(customRole)
      }
    }

    console.log('[getCurrentAuthUser] Extracted roles:', roles)

    const authUser: AuthUser = {
      userId: user.userId,
      email,
      tenantId,
      roles,
    }

    console.log('[getCurrentAuthUser] Returning user:', { userId: authUser.userId, email: authUser.email, tenantId: authUser.tenantId, roles: authUser.roles })

    return authUser
  } catch (error) {
    console.error('[getCurrentAuthUser] Error getting current user:', error)
    return null
  }
}

/**
 * Verifica si el usuario tiene un rol específico
 */
export async function hasRole(role: string): Promise<boolean> {
  try {
    const user = await getCurrentAuthUser()
    return user?.roles?.includes(role) || false
  } catch {
    return false
  }
}

/**
 * Verifica si el usuario tiene alguno de los roles especificados
 */
export async function hasAnyRole(roles: string[]): Promise<boolean> {
  try {
    const user = await getCurrentAuthUser()
    if (!user?.roles) return false
    return roles.some(role => user.roles?.includes(role))
  } catch {
    return false
  }
}












